import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { Easings, TweenGroup } from '../../core/Tween';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

/** Asset aliases registered by the host theme — checked before
 * falling back to drawn-Graphics card chrome. */
const CARD_BACK_ALIAS = 'card:back';
const CARD_FACE_ALIAS = 'card:face';
const SUIT_ALIASES: readonly { alias: string; isRed: boolean }[] = [
  { alias: 'card:spade',   isRed: false },
  { alias: 'card:diamond', isRed: true  },
  { alias: 'card:club',    isRed: false },
  { alias: 'card:heart',   isRed: true  },
];

export interface HigherLowerCardOptions {
  /** Card width in px. Height is 1.4× this. Default 130. */
  width?: number;
  /** Stroke colour for the card border. Default brand pink. */
  accentColor?: number;
}

/**
 * Single card widget — shows a face-up integer value, or a face-down
 * back. {@link reveal} flips face-down → face-up with a scale-x squash.
 * The back face uses the brand pattern; the front face is white with a
 * red/black numeral depending on whether the value is even (red) or
 * odd (black) — purely cosmetic, mirrors playing-card convention.
 */
export class HigherLowerCard extends Container {
  private readonly bg: Graphics;
  private readonly faceSprite: Sprite | null = null;
  private readonly numberText: Text;
  private readonly cornerText: Text;
  /** Back display — Sprite when {@code card:back} loaded; Graphics
   * fallback otherwise. {@link showBack} / {@link showFace} toggles
   * its visibility. */
  private readonly back: Container;
  /** Optional suit overlay below the centre numeral. Rotates between
   * spade/diamond/club/heart based on the card value so the same
   * value always lands on the same suit. */
  private suitSprite: Sprite | null = null;
  private readonly tweens = new TweenGroup();
  private readonly w: number;
  private readonly h: number;
  private readonly accentColor: number;

  constructor(value: number, options: HigherLowerCardOptions = {}) {
    super();
    this.w = options.width ?? 130;
    this.h = Math.floor(this.w * 1.4);
    this.accentColor = options.accentColor ?? 0xff6bd6;

    this.bg = new Graphics();
    this.addChild(this.bg);

    // Optional commissioned face template — covers the white card
    // body with a painterly texture. Rendered behind the numerals
    // and suit so they overlay cleanly.
    //
    // Producer audit (Phase 2.4): the painted assets live inside a
    // 1024×1024 canvas with significant transparent padding around
    // the actual card illustration. Force-setting sprite.width/height
    // to {@code w × h} distorts the visible card because Pixi scales
    // the WHOLE canvas (padding included). Use the texture's natural
    // dimensions to scale the sprite uniformly to the target height,
    // then accept whatever width comes out — the host layout is
    // height-anchored so this keeps the card proportions truthful.
    const faceTex = AssetLoader.shared.getTexture(CARD_FACE_ALIAS);
    if (faceTex) {
      const sprite = new Sprite(faceTex);
      sprite.anchor.set(0.5);
      const aspect = faceTex.width / faceTex.height;
      sprite.height = this.h;
      sprite.width = this.h * aspect;
      this.faceSprite = sprite;
      this.addChild(sprite);
    }

    this.numberText = new Text({
      text: '?',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(this.h * 0.42),
        fontWeight: '900',
        fill: 0x1a0830,
      },
    });
    this.numberText.anchor.set(0.5);
    this.addChild(this.numberText);

    this.cornerText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(this.h * 0.12),
        fontWeight: '900',
        fill: 0x1a0830,
      },
    });
    this.cornerText.anchor.set(0, 0);
    this.cornerText.x = -this.w / 2 + 8;
    this.cornerText.y = -this.h / 2 + 8;
    this.addChild(this.cornerText);

    // Card back — Sprite when texture loaded, Graphics fallback
    // otherwise. Added last so it sits on top of the face when
    // {@code visible}. Same aspect-preserving sizing as the face
    // template (see Phase 2.4 note above) so the back doesn't squish
    // when the painted asset's aspect differs from the legacy
    // {@code w × h} pill.
    const backTex = AssetLoader.shared.getTexture(CARD_BACK_ALIAS);
    if (backTex) {
      const sprite = new Sprite(backTex);
      sprite.anchor.set(0.5);
      const aspect = backTex.width / backTex.height;
      sprite.height = this.h;
      sprite.width = this.h * aspect;
      this.back = sprite;
    } else {
      const g = new Graphics();
      g.roundRect(-this.w / 2, -this.h / 2, this.w, this.h, 12)
        .fill({ color: 0x6b3d77, alpha: 0.95 })
        .stroke({ color: this.accentColor, alpha: 0.85, width: 3 });
      this.back = g;
    }
    this.addChild(this.back);

    this.paintFace(value);
    this.showBack();
  }

  /** Show the card back, hiding the face. Useful for "next card" before
   * reveal. */
  showBack(): void {
    this.back.visible = true;
  }

  /** Show the card face. Repaints if {@code value} is provided. */
  showFace(value?: number): void {
    if (value !== undefined) this.paintFace(value);
    this.back.visible = false;
  }

  /** Animated flip from back to face. {@code newValue} replaces the face
   * at the squash midpoint. Resolves when the flip completes. */
  reveal(newValue: number): Promise<void> {
    this.paintFace(newValue);
    let swapped = false;
    return this.tweens.startAsync({
      durationMs: 460,
      easing: Easings.easeInOutCubic,
      onUpdate: (p) => {
        const halfP = p < 0.5 ? p * 2 : (1 - p) * 2;
        this.scale.x = Math.max(0.05, halfP);
        if (p >= 0.5 && !swapped) {
          swapped = true;
          this.back.visible = false;
        }
      },
      onComplete: () => {
        this.scale.x = 1;
      },
    });
  }

  destroyCard(): void {
    this.tweens.cancelAll();
  }

  private paintFace(value: number): void {
    // Suit selection — deterministic by value so reveal/hide on the
    // same card lands on the same suit. Mirrors the prior
    // even-red / odd-black split via the SUIT_ALIASES table (spade,
    // diamond, club, heart in order).
    const suitMeta = SUIT_ALIASES[value % SUIT_ALIASES.length]!;
    const fill = suitMeta.isRed ? 0xd11538 : 0x1a1a2a;

    // Draw the white card body only when no commissioned face
    // template covered it — otherwise the sprite already provides
    // the body texture.
    this.bg.clear();
    if (!this.faceSprite) {
      this.bg
        .roundRect(-this.w / 2, -this.h / 2, this.w, this.h, 12)
        .fill({ color: 0xffffff, alpha: 0.96 })
        .stroke({ color: this.accentColor, alpha: 0.75, width: 3 });
    }

    this.numberText.text = `${value}`;
    this.numberText.style.fill = fill;
    this.cornerText.text = `${value}`;
    this.cornerText.style.fill = fill;

    // Add (or swap) the suit overlay. Only mounts when the suit
    // texture is registered — the test-scene fallback reads the
    // value's red/black colour through the numerals alone.
    const suitTex = AssetLoader.shared.getTexture(suitMeta.alias);
    if (suitTex) {
      if (!this.suitSprite) {
        const sprite = new Sprite(suitTex);
        sprite.anchor.set(0.5);
        const aspect = suitTex.width / suitTex.height;
        sprite.height = this.h * 0.18;
        sprite.width = sprite.height * aspect;
        // Sits below the centre numeral, above the bottom edge of
        // the card.
        sprite.x = 0;
        sprite.y = this.h * 0.28;
        this.addChildAt(sprite, this.children.indexOf(this.numberText));
        this.suitSprite = sprite;
      } else {
        this.suitSprite.texture = suitTex;
      }
    }
  }
}
